import { Play, Save, Trash2, X } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { resolvePreset, DateRangeFilter, type DateRange } from '@/components/shared/date-range-filter';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { MemberNameLink, memberIdForNameColumn } from '@/components/shared/member-name-link';
import { MoneyDisplay } from '@/components/shared/money-display';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormField } from '@/components/shared/form-field';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { toast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api-errors';
import { api } from '@/lib/api';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { canAccessReportsModule } from '@/lib/report-permissions';
import { useAuthStore } from '@/stores/auth-store';
import { useChurchStore } from '@/stores/church-store';
import { usePreferences } from '@/stores/preferences-store';
import { formatNumber } from '@/utils/format';

import { useCombinedExportPresets } from './combined-export-presets';
import {
  buildCombinedReportsPayload,
  deserializeDateRange,
  serializeDateRange,
  type ReportPickerValue,
  type SeededReportDefinition,
} from './combined-reports-utils';
import {
  downloadCombinedReportCsv,
  type CombinedReportRef,
} from './download-combined-report-csv';
import { downloadCombinedReportPdfExport } from './download-combined-report-pdf';
import { formatCustomReportCell } from './format-custom-report-cell';
import {
  formatReportCellValue,
  reportRowIsLiability,
  type ReportExportColumn,
} from './format-report-export';
import { getCustomReportSource } from './custom-report-sources';
import { ReportExportMenu } from './report-export-menu';
import { ReportFiltersBar } from './report-filters-bar';
import { ReportRunSkeleton } from './report-run-skeleton';
import { ReportsSubnav } from './reports-subnav';

interface ReportDefinition extends SeededReportDefinition {}

interface CustomReportRow {
  id: string;
  name: string;
}

interface CombinedRunSection {
  identity: string;
  ref: CombinedReportRef;
  title: string;
  nameKey?: string;
  kind: 'seeded' | 'custom';
  reportKey?: string;
  objectSource?: string;
  columns: ReportExportColumn[];
  rows: Record<string, unknown>[];
  page: { page: number; pageSize: number; totalItems: number; totalPages: number };
}

interface CombinedRunResult {
  sections: CombinedRunSection[];
}

const fromPickerValue = (value: ReportPickerValue): CombinedReportRef | null => {
  const [kind, id] = value.split(':') as ['seeded' | 'custom', string];
  if (kind === 'seeded') return { kind: 'seeded', key: id };
  return { kind: 'custom', id };
};

export const CombinedReportsPage = () => {
  const { t } = useTranslation('reports');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const membership = useCurrentMembership();
  const userId = useAuthStore((state) => state.user?.id);
  const church = useChurchStore((state) => state.church);
  const allowsFeature = useChurchStore((state) => state.allowsFeature);
  const locale = usePreferences((state) => state.locale);
  const timezone = usePreferences((state) => state.timezone);

  const canExport = hasPermission(membership, PERMISSIONS.REPORTS_EXPORT);
  const canView = canAccessReportsModule(membership);

  const [selectedValues, setSelectedValues] = useState<ReportPickerValue[]>([]);
  const [pickerValue, setPickerValue] = useState<ReportPickerValue | null>(null);
  const [sectionPages, setSectionPages] = useState<Record<string, number>>({});
  const [reportFilters, setReportFilters] = useState<Record<string, Record<string, string>>>({});
  const [loadedPresetId, setLoadedPresetId] = useState<string | null>(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [hasPreview, setHasPreview] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>(() => ({
    preset: 'thisMonth',
    ...resolvePreset('thisMonth', timezone),
  }));

  const { presets, savePreset, deletePreset } = useCombinedExportPresets(church?.id, userId);

  const seededReports = useQuery({
    queryKey: ['reports', 'list'],
    queryFn: () => api.get<{ data: ReportDefinition[] }>('/reports'),
  });

  const customReports = useQuery({
    queryKey: ['reports', 'custom', 'combined-picker'],
    enabled: allowsFeature('custom_reports'),
    queryFn: () =>
      api.get<{ data: CustomReportRow[] }>('/reports/custom', { query: { page: 1, pageSize: 100 } }),
  });

  const pickerOptions = useMemo(() => {
    const options: Array<{ value: ReportPickerValue; label: string; group: string }> = [];
    for (const report of seededReports.data?.data ?? []) {
      options.push({
        value: `seeded:${report.key}`,
        label: t(report.nameKey, report.key),
        group: t('combined.groupReadyMade'),
      });
    }
    for (const report of customReports.data?.data ?? []) {
      options.push({
        value: `custom:${report.id}`,
        label: report.name,
        group: t('combined.groupCustom'),
      });
    }
    return options;
  }, [customReports.data?.data, seededReports.data?.data, t]);

  const optionLabelByValue = useMemo(
    () => new Map(pickerOptions.map((option) => [option.value, option.label])),
    [pickerOptions],
  );

  const seededByKey = useMemo(
    () => new Map((seededReports.data?.data ?? []).map((report) => [report.key, report])),
    [seededReports.data?.data],
  );

  const selectedRefs = useMemo(
    () =>
      selectedValues
        .map((value) => fromPickerValue(value))
        .filter((ref): ref is CombinedReportRef => ref !== null),
    [selectedValues],
  );

  const selectedSeeded = useMemo(
    () =>
      selectedValues
        .map((value) => {
          if (!value.startsWith('seeded:')) return null;
          const key = value.slice('seeded:'.length);
          return seededByKey.get(key) ?? null;
        })
        .filter((report): report is ReportDefinition => report !== null),
    [seededByKey, selectedValues],
  );

  const selectedSeededWithFilters = useMemo(
    () => selectedSeeded.filter((report) => (report.filters?.length ?? 0) > 0),
    [selectedSeeded],
  );

  const reportsPayload = useMemo(
    () => buildCombinedReportsPayload(selectedRefs, reportFilters, seededByKey),
    [reportFilters, seededByKey, selectedRefs],
  );

  const showDateRange = selectedSeeded.some((report) => report.supportsDateRange);

  const dateRangeLabels = useMemo(
    () => ({
      today: tCommon('date.today'),
      yesterday: tCommon('date.yesterday'),
      thisWeek: tCommon('date.thisWeek'),
      lastWeek: tCommon('date.lastWeek'),
      thisMonth: tCommon('date.thisMonth'),
      lastMonth: tCommon('date.lastMonth'),
      thisQuarter: tCommon('date.thisQuarter'),
      thisYear: tCommon('date.thisYear'),
      lastYear: tCommon('date.lastYear'),
      allTime: tCommon('date.allTime'),
      custom: tCommon('date.custom'),
      from: t('run.dateFrom'),
      to: t('run.dateTo'),
    }),
    [t, tCommon],
  );

  const availableOptions = useMemo(
    () => pickerOptions.filter((option) => !selectedValues.includes(option.value)),
    [pickerOptions, selectedValues],
  );

  const reportsReady = selectedRefs.length > 0;

  useEffect(() => {
    setSectionPages({});
    setHasPreview(false);
  }, [selectedValues, dateRange.from, dateRange.to, showDateRange, reportFilters]);

  const run = useQuery({
    queryKey: [
      'reports',
      'combined',
      'run',
      selectedValues,
      sectionPages,
      reportFilters,
      showDateRange ? dateRange.from : null,
      showDateRange ? dateRange.to : null,
    ],
    enabled: false,
    refetchOnWindowFocus: false,
    queryFn: () =>
      api.post<CombinedRunResult>('/reports/combined/run', {
        reports: reportsPayload,
        dateFrom: showDateRange ? (dateRange.from ?? undefined) : undefined,
        dateTo: showDateRange ? (dateRange.to ?? undefined) : undefined,
        pageSize: 25,
        pages: sectionPages,
      }),
  });

  useEffect(() => {
    if (!hasPreview || !reportsReady) return;
    void run.refetch();
    // Refetch when the user pages a preview section after the initial run.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only when sectionPages changes
  }, [sectionPages]);

  const handleRunPreview = () => {
    if (!reportsReady) return;
    setHasPreview(true);
    void run.refetch();
  };

  const addReport = (value: ReportPickerValue | null) => {
    if (!value || selectedValues.includes(value)) {
      setPickerValue(null);
      return;
    }
    setSelectedValues((current) => [...current, value]);
    setPickerValue(null);
    if (loadedPresetId) setLoadedPresetId(null);
  };

  const removeReport = (value: ReportPickerValue) => {
    setSelectedValues((current) => current.filter((item) => item !== value));
    setReportFilters((current) => {
      if (!(value in current)) return current;
      const next = { ...current };
      delete next[value];
      return next;
    });
    if (loadedPresetId) setLoadedPresetId(null);
  };

  const clearAllReports = () => {
    setSelectedValues([]);
    setReportFilters({});
    setPickerValue(null);
    setLoadedPresetId(null);
    setSectionPages({});
    setHasPreview(false);
  };

  const reportsFieldId = useId();

  const updateReportFilter = (identity: string, key: string, value: string) => {
    setReportFilters((current) => ({
      ...current,
      [identity]: { ...(current[identity] ?? {}), [key]: value },
    }));
    if (loadedPresetId) setLoadedPresetId(null);
  };

  const loadPreset = (presetId: string | null) => {
    if (!presetId) {
      setLoadedPresetId(null);
      return;
    }
    const preset = presets.find((item) => item.id === presetId);
    if (!preset) return;
    setSelectedValues(preset.selectedValues);
    setReportFilters(preset.reportFilters);
    setDateRange(deserializeDateRange(preset.dateRange, timezone, resolvePreset));
    setLoadedPresetId(preset.id);
    setSectionPages({});
  };

  const handleSavePreset = () => {
    const name = presetName.trim();
    if (!name || selectedValues.length === 0) return;
    const isUpdate = Boolean(loadedPresetId);
    const id = loadedPresetId ?? crypto.randomUUID();
    savePreset({
      id,
      name,
      selectedValues,
      dateRange: serializeDateRange(dateRange),
      reportFilters,
    });
    setLoadedPresetId(id);
    setSaveDialogOpen(false);
    setPresetName('');
    toast.success(t(isUpdate ? 'combined.updatedSavedExportToast' : 'combined.savedPreset'));
  };

  const exportMutation = useMutation({
    mutationFn: async (format: 'csv' | 'pdf') => {
      if (!church?.id || reportsPayload.length === 0) return;
      const dateFrom = showDateRange ? (dateRange.from ?? undefined) : undefined;
      const dateTo = showDateRange ? (dateRange.to ?? undefined) : undefined;

      if (format === 'csv') {
        await downloadCombinedReportCsv({
          churchId: church.id,
          reports: reportsPayload,
          dateFrom,
          dateTo,
          fallbackFilename: 'combined-report.csv',
        });
        return;
      }

      const periodLabel =
        showDateRange && (dateFrom || dateTo)
          ? t('run.pdfPeriod', {
              from: dateFrom ?? tCommon('date.allTime'),
              to: dateTo ?? tCommon('date.allTime'),
            })
          : undefined;

      await downloadCombinedReportPdfExport({
        churchId: church.id,
        reports: reportsPayload,
        dateFrom,
        dateTo,
        churchName: church.name,
        branchName: church.campusName,
        logoUrl: church.logoUrl,
        locale,
        timezone,
        periodLabel,
        t,
        tCommon,
      });
    },
    onSuccess: (_, format) => {
      toast.success(format === 'csv' ? t('run.exportDownloaded') : t('run.exportPdfDownloaded'));
    },
    onError: (error) => {
      if (error instanceof ApiError) toast.error(tErrors(error.code, error.params));
      else toast.error(tCommon('toast.somethingWentWrong'));
    },
  });

  const sectionTitle = (section: CombinedRunSection) =>
    section.kind === 'seeded' && section.nameKey
      ? t(section.nameKey, section.reportKey ?? section.title)
      : section.title;

  const columnLabel = (section: CombinedRunSection, key: string) =>
    section.kind === 'custom'
      ? t(`custom.fields.${key}`, key)
      : t(`column.${key}`, key);

  const formatSeededCell = (
    column: ReportExportColumn,
    value: unknown,
    row: Record<string, unknown>,
  ) => {
    const memberId = memberIdForNameColumn(column.key, row);
    if (memberId && typeof value === 'string' && value.trim()) {
      return <MemberNameLink memberId={memberId} name={value} />;
    }
    if (value === null || value === undefined || value === '') return '—';
    if (column.type === 'money') {
      const currency = typeof row.currency === 'string' ? row.currency : 'GHS';
      return (
        <MoneyDisplay
          amount={Number(value)}
          currency={currency}
          colorNegative
          className={reportRowIsLiability(row) ? 'text-danger-emphasis' : undefined}
        />
      );
    }
    if (column.type === 'number') return formatNumber(Number(value), locale);
    return formatReportCellValue(column, value, row, t, { locale });
  };

  const formatCustomCell = (
    section: CombinedRunSection,
    column: ReportExportColumn,
    value: unknown,
    row: Record<string, unknown>,
  ) => {
    const memberId = memberIdForNameColumn(column.key, row);
    if (memberId && typeof value === 'string' && value.trim()) {
      return <MemberNameLink memberId={memberId} name={value} />;
    }
    const source = getCustomReportSource(section.objectSource ?? '');
    const field = source?.fields.find((item) => item.key === column.key);
    return formatCustomReportCell(field, value, row, t, locale);
  };

  return (
    <div className="space-y-6">
      <PageHeader title={t('combined.title')} description={t('combined.description')} />
      <ReportsSubnav />

      <Card>
        <CardContent className="space-y-6 pt-6">
          {presets.length > 0 ? (
            <FormField label={t('combined.savedExports')}>
              {(field) => (
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                  <Listbox
                    {...field}
                    className="sm:flex-1"
                    value={loadedPresetId}
                    onChange={loadPreset}
                    options={presets.map((preset) => ({
                      value: preset.id,
                      label: preset.name,
                    }))}
                    placeholder={t('combined.loadSavedExport')}
                    searchable
                    searchPlaceholder={tCommon('search.placeholder')}
                    emptyMessage={tCommon('search.noResults')}
                    clearable
                    clearLabel={tCommon('action.clearSelection')}
                  />
                  {loadedPresetId ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="shrink-0"
                      aria-label={t('combined.deleteSavedExport')}
                      onClick={() => {
                        deletePreset(loadedPresetId);
                        setLoadedPresetId(null);
                        toast.success(t('combined.deletedSavedExport'));
                      }}
                    >
                      <Trash2 className="size-4" aria-hidden />
                    </Button>
                  ) : null}
                </div>
              )}
            </FormField>
          ) : null}

          <div className="space-y-1.5">
            <div className="flex items-baseline justify-between gap-2">
              <Label htmlFor={reportsFieldId}>{t('combined.reports')}</Label>
              {selectedValues.length > 0 ? (
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto shrink-0 px-0"
                  onClick={clearAllReports}
                >
                  {t('combined.clearAllReports')}
                </Button>
              ) : null}
            </div>
            <div className="flex flex-col gap-3">
              {selectedValues.length > 0 ? (
                <ul className="flex flex-col gap-2">
                  {selectedValues.map((value) => {
                    const label = optionLabelByValue.get(value) ?? value;
                    return (
                      <li
                        key={value}
                        className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface-sunken px-3 py-2"
                      >
                        <span className="min-w-0 truncate text-body-sm text-fg-primary">
                          {label}
                        </span>
                        <button
                          type="button"
                          className="shrink-0 rounded-full p-1 text-fg-muted hover:text-fg"
                          aria-label={t('combined.removeReport', { name: label })}
                          onClick={() => removeReport(value)}
                        >
                          <X className="size-4" aria-hidden />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-body-sm text-fg-secondary">{t('combined.emptyReports')}</p>
              )}

              {availableOptions.length > 0 ? (
                <Listbox
                  id={reportsFieldId}
                  value={pickerValue}
                  onChange={addReport}
                  options={availableOptions}
                  placeholder={t('combined.addReport')}
                  searchable
                  searchPlaceholder={tCommon('search.placeholder')}
                  emptyMessage={tCommon('search.noResults')}
                  clearLabel={tCommon('action.clearSelection')}
                />
              ) : null}
            </div>
          </div>

          {showDateRange ? (
            <FormField label={t('run.dateRange')}>
              {() => (
                <DateRangeFilter
                  value={dateRange}
                  onChange={(next) => {
                    setDateRange(next);
                    if (loadedPresetId) setLoadedPresetId(null);
                  }}
                  labels={dateRangeLabels}
                />
              )}
            </FormField>
          ) : null}

          {selectedSeededWithFilters.length > 0 ? (
            <div className="space-y-4">
              <p className="text-body-sm font-medium text-fg-primary">{t('combined.filters')}</p>
              {selectedSeededWithFilters.map((report) => {
                const identity = `seeded:${report.key}`;
                return (
                  <div
                    key={report.key}
                    className="space-y-3 rounded-md border border-border bg-surface-sunken p-4"
                  >
                    <p className="text-body-sm font-medium text-fg-primary">
                      {t(report.nameKey, report.key)}
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <ReportFiltersBar
                        filters={report.filters ?? []}
                        values={reportFilters[identity] ?? {}}
                        onChange={(key, value) => updateReportFilter(identity, key, value)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}

          <p className="text-body-sm text-fg-secondary">{t('combined.hint')}</p>

          <div className="flex flex-wrap justify-end gap-2">
            {reportsReady ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setPresetName(
                    loadedPresetId
                      ? (presets.find((item) => item.id === loadedPresetId)?.name ?? '')
                      : '',
                  );
                  setSaveDialogOpen(true);
                }}
              >
                <Save className="size-4" aria-hidden />
                {loadedPresetId ? t('combined.updateSavedExport') : t('combined.saveExport')}
              </Button>
            ) : null}
            {canView ? (
              <Button
                variant="secondary"
                onClick={handleRunPreview}
                disabled={!reportsReady}
                loading={run.isFetching}
              >
                <Play className="size-4" aria-hidden />
                {t('run.run')}
              </Button>
            ) : null}
            {canExport ? (
              <ReportExportMenu
                disabled={!reportsReady}
                loading={exportMutation.isPending}
                format={exportMutation.variables}
                variant="primary"
                label={t('combined.export')}
                onExport={(format) => exportMutation.mutate(format)}
              />
            ) : null}
          </div>
        </CardContent>
      </Card>

      {canView && hasPreview && reportsReady ? (
        run.isFetching ? (
          <ReportRunSkeleton
            columnCount={run.data?.sections[0]?.columns.length || 3}
          />
        ) : run.isError ? (
          <Card>
            <CardContent className="py-6 text-body-sm text-danger">
              {tCommon('toast.somethingWentWrong')}
            </CardContent>
          </Card>
        ) : run.data ? (
          <div className="space-y-6">
            {run.data.sections.map((section) => (
              <Card key={section.identity}>
                <CardContent className="space-y-4 pt-6">
                  <h2 className="text-card-title text-fg">{sectionTitle(section)}</h2>
                  <div className="overflow-x-auto rounded-md border border-border">
                    <table className="w-full text-body-sm">
                      <thead>
                        <tr className="border-b border-border text-left text-caption text-fg-secondary">
                          {section.columns.map((column) => (
                            <th key={column.key} className="px-4 py-3 font-medium">
                              {columnLabel(section, column.key)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {section.rows.length === 0 ? (
                          <tr>
                            <td
                              colSpan={section.columns.length}
                              className="px-4 py-8 text-center text-fg-muted"
                            >
                              {tCommon('table.noResults')}
                            </td>
                          </tr>
                        ) : (
                          section.rows.map((row, index) => (
                            <tr
                              key={String(row.id ?? `${section.identity}-${index}`)}
                              className="border-b border-border last:border-0"
                            >
                              {section.columns.map((column) => (
                                <td key={column.key} className="px-4 py-3 align-top">
                                  {section.kind === 'custom'
                                    ? formatCustomCell(section, column, row[column.key], row)
                                    : formatSeededCell(column, row[column.key], row)}
                                </td>
                              ))}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <Pagination
                    page={section.page.page}
                    pageSize={section.page.pageSize}
                    totalItems={section.page.totalItems}
                    totalPages={section.page.totalPages}
                    onPageChange={(page) =>
                      setSectionPages((current) => ({ ...current, [section.identity]: page }))
                    }
                    labels={{
                      showing: ({ from, to, total }) =>
                        tCommon('pagination.showing', { from, to, total }),
                      perPage: tCommon('pagination.perPage'),
                      first: tCommon('pagination.first'),
                      previous: tCommon('pagination.previous'),
                      next: tCommon('pagination.next'),
                      last: tCommon('pagination.last'),
                    }}
                  />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : null
      ) : null}

      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {loadedPresetId ? t('combined.updateSavedExport') : t('combined.saveExport')}
            </DialogTitle>
            <DialogDescription>{t('combined.saveExportDescription')}</DialogDescription>
          </DialogHeader>
          <DialogBody contentClassName="flex flex-col gap-4">
            <FormField label={t('combined.savedExportName')} required>
              {(field) => (
                <Input
                  id={field.id}
                  value={presetName}
                  onChange={(event) => setPresetName(event.target.value)}
                  aria-describedby={field['aria-describedby']}
                  aria-invalid={field.invalid}
                  autoFocus
                  required
                />
              )}
            </FormField>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setSaveDialogOpen(false)}>
              {tCommon('action.cancel')}
            </Button>
            <Button
              type="button"
              disabled={!presetName.trim() || selectedValues.length === 0}
              onClick={handleSavePreset}
            >
              {tCommon('action.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
